import React from 'react';
import { Flex, Menu, Tooltip, Typography } from 'antd';
import { Dropdown } from '@/components/AntdCompatDropdown';
import styles from './CircleGroup.module.css';

interface User {
  id?: string;
  user?: string;
  role?: string;
  email?: string;
  badge_color?: string;
}

interface UserTooltipProps {
  user: User;
  index: number;
  children: React.ReactNode;
}

interface CircleGroupProps {
  viewers?: User[];
}

const { Text } = Typography;

/**
 * Gets a color from the user color palette based on index
 * @param index - The index to use for color selection (defaults to 0)
 * @returns The color code from the palette
 */
const getBadgeColor = (user?: User): string => user?.badge_color || '#ccc';
/**
 * Extracts initials from a user's name
 * @param name - The full name to extract initials from (defaults to empty string)
 * @returns The uppercase initials (up to 2 characters)
 */
const getInitials = (name = ''): string =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((n) => n[0] || '')
    .join('')
    .toUpperCase();

/**
 * Tooltip component that displays user information on hover
 * @param user - User object containing name, role, and email
 * @param index - Index used for avatar color assignment
 * @param children - Child components to wrap with tooltip
 */
const UserTooltip: React.FC<UserTooltipProps> = ({ user, children }) => (
  <Tooltip
    color='#fff'
    overlayInnerStyle={{
      background: 'var(--surface-container)',
      padding: 0,
      borderRadius: 12,
    }}
    overlayStyle={{ borderRadius: 12, minWidth: 320 }}
    title={
      <div className={styles.tooltipCard}>
        <Flex
          justify='space-between'
          align='center'
          style={{ width: '100%', gap: 12 }}
        >
          <div className={styles.tooltipText}>
            <Text strong style={{ fontSize: 18 }}>
              {user?.user}
            </Text>
            {user?.role && (
              <span className={styles.tooltipRole}>{user.role}</span>
            )}
            {user?.email && (
              <span className={styles.tooltipEmail}>{user.email}</span>
            )}
          </div>
          <div
            className={`${styles.avatar} ${styles.avatarTooltip}`}
            style={{
              backgroundColor: getBadgeColor(user),
            }}
          >
            {getInitials(user?.user)}
          </div>
        </Flex>
      </div>
    }
  >
    {children}
  </Tooltip>
);

/**
 * Component that displays a group of user avatars in overlapping circles
 *
 * Shows up to 2 users as individual avatars with tooltips containing their details.
 * Additional users (beyond the first 2) are displayed in a dropdown accessible via
 * a "+N" indicator. Each avatar displays the user's initials with a color-coded background.
 *
 * @param viewers - Array of viewer objects with user information
 */
const CircleGroup: React.FC<CircleGroupProps> = ({ viewers = [] }) => {
  const visibleUsers = viewers.slice(0, 2);
  const extraUsers = viewers.slice(2);

  return (
    <Flex className={styles.circleGroup}>
      {visibleUsers.map((user, index) => (
        <UserTooltip key={user.id} user={user} index={index}>
          <div
            className={`${styles.avatar} ${styles.avatarMd}`}
            style={{
              backgroundColor: getBadgeColor(user),
              marginLeft: index === 0 ? 0 : -10,
              cursor: 'pointer',
            }}
          >
            {getInitials(user?.user)}
          </div>
        </UserTooltip>
      ))}

      {extraUsers.length > 0 && (
        <Dropdown
          overlay={
            <Menu className={styles.dropdownMenu}>
              {extraUsers.map((user) => (
                <Menu.Item key={user.id} className={styles.dropdownItem}>
                  <Flex
                    justify='space-between'
                    align='center'
                    className={styles.dropdownRow}
                  >
                    <div className={styles.tooltipText}>
                      <Text strong style={{ fontSize: 18 }}>
                        {user?.user}
                      </Text>
                      {user?.role && (
                        <span className={styles.tooltipRole}>{user.role}</span>
                      )}
                      {user?.email && (
                        <span className={styles.tooltipEmail}>
                          {user.email}
                        </span>
                      )}
                    </div>
                    <div
                      className={`${styles.avatar} ${styles.avatarTooltip}`}
                      style={{
                        backgroundColor: getBadgeColor(user),
                      }}
                    >
                      {getInitials(user?.user)}
                    </div>
                  </Flex>
                </Menu.Item>
              ))}
            </Menu>
          }
          trigger={['hover']}
          mouseEnterDelay={0.05}
          mouseLeaveDelay={0.1}
          destroyPopupOnHide
        >
          <div
            className={`${styles.avatar} ${styles.avatarMd} ${styles.avatarDark}`}
            style={{ marginLeft: -10, cursor: 'pointer' }}
          >
            +{extraUsers.length}
          </div>
        </Dropdown>
      )}
    </Flex>
  );
};

export default CircleGroup;
